import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { DocumentDuplicateIcon, ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { downloadVaAccountsCsv } from '../../utils/vaAccountCsvExport';

const STATUSES = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'captcha', label: 'Captcha' },
  { id: 'soft_ban', label: 'Soft ban' },
  { id: 'limited', label: 'Limited' }
];

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  suspended: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  captcha: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  soft_ban: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  limited: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
};

const CopyCell = ({ value, label }) => (
  <div className="flex items-center gap-2 w-full">
    <span
      className="flex-1 min-w-0 font-mono text-[11px] text-slate-800 dark:text-slate-200 truncate"
      title={value || ''}
    >
      {value || '—'}
    </span>
    {value && (
      <button
        type="button"
        title={`Copy ${label}`}
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success(`${label} copied`);
        }}
        className="flex-shrink-0 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400"
      >
        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

const PlusUsernameCell = ({ item, onSaved }) => {
  const [value, setValue] = useState(item.plusUsername || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(item.plusUsername || '');
  }, [item._id, item.plusUsername]);

  const save = async () => {
    const normalized = String(value || '').replace(/^@/, '').trim();
    if (normalized === String(item.plusUsername || '').trim()) return;
    setSaving(true);
    try {
      const res = await api.patch(`/va/profile-accounts/${item._id}/plus-username`, {
        plusUsername: normalized
      });
      const saved = res.data?.item?.plusUsername ?? normalized;
      toast.success('+Username saved');
      onSaved?.(item._id, saved);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save +Username');
      setValue(item.plusUsername || '');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1 min-w-[120px]">
      <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex-shrink-0">+</span>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/^@/, ''))}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        placeholder="username"
        disabled={saving}
        className="w-full min-w-0 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1 font-mono text-[11px] text-slate-800 dark:text-slate-200 disabled:opacity-60"
        title="Updated X username (used for follower/following stats)"
      />
    </div>
  );
};

const VAProfileAccounts = () => {
  const [items, setItems] = useState([]);
  const [adminPreviewMessage, setAdminPreviewMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('active');
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/va/profile-accounts', {
        params: {
          status: statusFilter,
          search: search.trim() || undefined,
          limit: 200
        }
      });
      if (res.data.success) {
        setItems(res.data.items || []);
        setAdminPreviewMessage(res.data.message || '');
      }
    } catch (err) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItems();
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchItems]);

  useEffect(() => {
    setSelected(new Set());
  }, [statusFilter, search]);

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i._id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const applyBulkStatus = async () => {
    if (selected.size === 0) return;
    try {
      await api.patch('/va/profile-accounts/bulk-status', {
        ids: Array.from(selected),
        status: bulkStatus
      });
      toast.success(`Updated ${selected.size} account(s)`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk update failed');
    }
  };

  const updateOneStatus = async (id, status) => {
    try {
      await api.patch(`/va/profile-accounts/${id}/status`, { status });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  const exportSelectedCsv = () => {
    if (selected.size === 0) {
      toast.error('Select at least one account to export');
      return;
    }
    const rows = items.filter((item) => selected.has(item._id));
    downloadVaAccountsCsv(rows);
    toast.success(`Exported ${rows.length} account(s) to CSV`);
  };

  const handlePlusUsernameSaved = (id, plusUsername) => {
    setItems((prev) => prev.map((row) => (
      row._id === id ? { ...row, plusUsername } : row
    )));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStatusFilter(s.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              statusFilter === s.id
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search username or +username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 pl-9 pr-3 py-2 text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} className="rounded" />
          Select all ({selected.size}/{items.length})
        </label>
        <select
          value={bulkStatus}
          onChange={(e) => setBulkStatus(e.target.value)}
          className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
        >
          {STATUSES.filter((s) => s.id !== 'all').map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <button type="button" onClick={applyBulkStatus} disabled={selected.size === 0} className="btn-primary !py-2 disabled:opacity-40">
          Apply to selected
        </button>
        <button
          type="button"
          onClick={exportSelectedCsv}
          disabled={selected.size === 0}
          className="inline-flex items-center gap-1.5 btn-secondary !py-2 text-sm disabled:opacity-40"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export CSV ({selected.size})
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {adminPreviewMessage || (search.trim() ? 'No accounts match your search.' : 'No accounts assigned yet.')}
          </div>
        ) : (
          <table className="w-full table-fixed text-[11px] whitespace-nowrap">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-left uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-2 w-8" />
                <th className="px-2 py-2">Username</th>
                <th className="px-2 py-2">+Username</th>
                <th className="px-2 py-2">Password</th>
                <th className="px-2 py-2">Phone</th>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">AUTH</th>
                <th className="px-2 py-2">Proxy</th>
                <th className="px-2 py-2">Following</th>
                <th className="px-2 py-2">Followers</th>
                <th className="px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((item) => (
                <tr key={item._id} className="align-top">
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={selected.has(item._id)} onChange={() => toggleOne(item._id)} className="rounded" />
                  </td>
                  <td className="px-2 py-2"><CopyCell value={item.username} label="Username" /></td>
                  <td className="px-2 py-2">
                    <PlusUsernameCell item={item} onSaved={handlePlusUsernameSaved} />
                  </td>
                  <td className="px-2 py-2"><CopyCell value={item.password} label="Password" /></td>
                  <td className="px-2 py-2"><CopyCell value={item.phone} label="Phone" /></td>
                  <td className="px-2 py-2"><CopyCell value={item.email} label="Email" /></td>
                  <td className="px-2 py-2"><CopyCell value={item.authToken} label="AUTH" /></td>
                  <td className="px-2 py-2"><CopyCell value={item.proxy} label="Proxy" /></td>
                  <td className="px-2 py-2 text-slate-600 dark:text-slate-300">
                    <div className="flex items-center justify-between gap-2">
                      <span className="tabular-nums">{(item.followingCount || 0).toLocaleString()}</span>
                      {item.performanceFromBaseline?.followingDelta != null && (
                        <span className="text-[11px] text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          {item.performanceFromBaseline.followingDelta >= 0 ? '+' : ''}
                          {item.performanceFromBaseline.followingDelta} since start
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-slate-600 dark:text-slate-300">
                    <div className="flex items-center justify-between gap-2">
                      <span className="tabular-nums">{(item.followersCount || 0).toLocaleString()}</span>
                      {item.performanceFromBaseline?.followersDelta != null && (
                        <span className="text-[11px] text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          {item.performanceFromBaseline.followersDelta >= 0 ? '+' : ''}
                          {item.performanceFromBaseline.followersDelta} since start
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={item.status}
                      onChange={(e) => updateOneStatus(item._id, e.target.value)}
                      className={`rounded-lg px-2 py-1 text-[11px] font-medium border-0 ${STATUS_COLORS[item.status] || ''}`}
                    >
                      {STATUSES.filter((s) => s.id !== 'all').map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VAProfileAccounts;
